import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import AppShell from '../components/home/AppShell';
import { Clock, Coffee, Activity, Zap, Gauge, CheckCircle2, Monitor, Shield, Info } from 'lucide-react';
import LeaveSection from '../components/workforce/LeaveSection';
import { cn } from '../lib/utils';

const API = process.env.REACT_APP_BACKEND_URL;

const EVENT_ICON = {
  check_in: CheckCircle2, break_start: Coffee, break_end: Coffee,
  finish: Clock, idle: Clock, module: Activity,
};
const EVENT_TONE = {
  check_in: 'text-emerald-600 bg-emerald-50', break_start: 'text-amber-600 bg-amber-50',
  break_end: 'text-amber-600 bg-amber-50', finish: 'text-slate-600 bg-slate-100',
  idle: 'text-orange-500 bg-orange-50', module: 'text-[#3A6AFF] bg-blue-50',
};

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' }) : '—');

const MyActivity = () => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [week, setWeek] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const h = { Authorization: `Bearer ${token}` };
      const [a, w] = await Promise.all([
        fetch(`${API}/api/workforce/me/activity`, { headers: h }).then((r) => r.json()),
        fetch(`${API}/api/workforce/me/week`, { headers: h }).then((r) => r.json()),
      ]);
      setData(a); setWeek(w);
    } catch { /* keep last */ } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); const id = setInterval(load, 30000); return () => clearInterval(id); }, [load]);

  const s = data?.session;

  return (
    <AppShell activeKey="my-activity">
      <div className="max-w-[1080px] mx-auto px-6 sm:px-8 py-7" data-testid="my-activity-page">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[13px] text-slate-400">{new Date().toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
            <h1 className="text-[26px] sm:text-[30px] font-bold tracking-tight mt-0.5">My Activity</h1>
          </div>
        </div>

        <div className="mt-4 flex items-start gap-2.5 rounded-xl border border-slate-200 bg-white px-4 py-3 text-[12.5px] text-slate-600" data-testid="transparency-note">
          <Shield className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
          <p>Full transparency — this is exactly what's recorded about you. We never capture passwords, keystrokes, message contents or screenshots. Only your DerwentAI modules, active/idle time and work outcomes.</p>
        </div>

        <div className="mt-5">
          <LeaveSection />
        </div>

        {loading && !data ? (
          <p className="mt-8 text-slate-400 text-sm">Loading your activity…</p>
        ) : !s ? (
          <div className="mt-6 rounded-xl border border-slate-200 bg-white p-8 text-center" data-testid="my-activity-empty">
            <Clock className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <p className="text-slate-600 font-medium">You haven't checked in today.</p>
            <p className="text-[13px] text-slate-400 mt-1">Use the status control in the bottom-right corner to check in.</p>
          </div>
        ) : (
          <>
            {/* Today stats */}
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="my-activity-stats">
              <StatCard icon={CheckCircle2} label="Checked In" value={fmtTime(s.checked_in_at)} tone="emerald" />
              <StatCard icon={Clock} label="Worked" value={s.work_label} tone="blue" />
              <StatCard icon={Coffee} label="Break" value={s.break_label} tone="amber" />
              <StatCard icon={Zap} label="Active" value={s.active_label} tone="emerald" />
              <StatCard icon={Activity} label="Idle" value={s.idle_label} tone="orange" />
              <StatCard icon={Gauge} label="Productivity" value={`${s.productivity}%`} tone="violet" />
            </div>

            <div className="mt-6 grid grid-cols-1 lg:grid-cols-5 gap-5">
              {/* Timeline */}
              <section className="lg:col-span-3 rounded-xl border border-slate-200 bg-white" data-testid="my-activity-timeline">
                <h2 className="text-[15px] font-semibold px-5 pt-4 pb-3 border-b border-slate-100">Today's Timeline</h2>
                <div className="p-5 space-y-3 max-h-[480px] overflow-y-auto">
                  {(s.events || []).length === 0 && <p className="text-[13px] text-slate-400">No activity yet.</p>}
                  {(s.events || []).map((e) => {
                    const Icon = EVENT_ICON[e.type] || Activity;
                    return (
                      <div key={e.id} className="flex items-start gap-3" data-testid={`timeline-event-${e.type}`}>
                        <span className="text-[12px] tabular-nums text-slate-400 w-16 shrink-0 pt-0.5">{fmtTime(e.at)}</span>
                        <span className={cn('w-7 h-7 rounded-lg flex items-center justify-center shrink-0', EVENT_TONE[e.type] || 'text-slate-500 bg-slate-100')}>
                          <Icon className="w-3.5 h-3.5" />
                        </span>
                        <span className="text-[13px] text-slate-700 pt-1">{e.label}</span>
                      </div>
                    );
                  })}
                </div>
              </section>

              {/* Right column */}
              <div className="lg:col-span-2 space-y-5">
                <section className="rounded-xl border border-slate-200 bg-white" data-testid="my-activity-modules">
                  <h2 className="text-[15px] font-semibold px-5 pt-4 pb-3 border-b border-slate-100">DerwentAI Activity</h2>
                  <div className="p-5 space-y-2.5">
                    {(s.modules || []).length === 0 && <p className="text-[13px] text-slate-400">No modules used yet.</p>}
                    {(s.modules || []).map((m) => (
                      <div key={m.module} className="flex items-center justify-between text-[13px]" data-testid={`module-row-${m.module}`}>
                        <span className="text-slate-700">{m.module}</span>
                        <span className="font-semibold text-slate-900 tabular-nums">{m.label}</span>
                      </div>
                    ))}
                  </div>
                </section>

                <section className="rounded-xl border border-dashed border-slate-300 bg-slate-50/60 p-5" data-testid="desktop-agent-placeholder">
                  <div className="flex items-center gap-2 text-slate-500">
                    <Monitor className="w-4 h-4" />
                    <span className="text-[13px] font-semibold">Applications & Websites</span>
                  </div>
                  <p className="text-[12px] text-slate-400 mt-2">Tracking of desktop apps and websites (Outlook, Chrome, lender portals) requires the DerwentAI Desktop Agent. <span className="font-medium text-slate-500">Coming soon.</span></p>
                </section>
              </div>
            </div>

            {/* Week */}
            {week && (
              <section className="mt-6 rounded-xl border border-slate-200 bg-white" data-testid="my-activity-week">
                <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-slate-100">
                  <h2 className="text-[15px] font-semibold">This Week</h2>
                  <div className="flex items-center gap-4 text-[12px] text-slate-500">
                    <span>Worked <b className="text-slate-900">{week.totals.work_label}</b></span>
                    <span>Active <b className="text-slate-900">{week.totals.active_label}</b></span>
                    <span>Breaks <b className="text-slate-900">{week.totals.break_label}</b></span>
                  </div>
                </div>
                <div className="grid grid-cols-7 divide-x divide-slate-100">
                  {week.days.map((d) => (
                    <div key={d.date} className="px-2 py-3 text-center" data-testid={`week-day-${d.date}`}>
                      <p className="text-[10px] font-semibold uppercase text-slate-400">{new Date(d.date).toLocaleDateString('en-AU', { weekday: 'short' })}</p>
                      <p className="text-[13px] font-bold text-slate-800 mt-1 tabular-nums">{d.work_label}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            <p className="mt-4 text-[11px] text-slate-400 flex items-center gap-1.5"><Info className="w-3 h-3" /> Business-outcome metrics (files reviewed, documents, tasks completed) wire into this view in the next phase.</p>
          </>
        )}
      </div>
    </AppShell>
  );
};

const TONE = {
  emerald: 'text-emerald-600 bg-emerald-50', blue: 'text-[#3A6AFF] bg-blue-50',
  amber: 'text-amber-600 bg-amber-50', orange: 'text-orange-500 bg-orange-50',
  violet: 'text-violet-600 bg-violet-50',
};
const StatCard = ({ icon: Icon, label, value, tone }) => (
  <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
    <div className={cn('w-7 h-7 rounded-lg flex items-center justify-center mb-2', TONE[tone])}><Icon className="w-4 h-4" /></div>
    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</p>
    <p className="text-[18px] font-bold text-slate-900 mt-0.5 tabular-nums">{value}</p>
  </div>
);

export default MyActivity;
